import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star, Award, TrendingUp, Clock, Shield, Users,
  ThumbsUp, Zap, AlertTriangle, Search, Filter, Edit,
  BarChart2, ArrowUpRight, Eye, X, Calendar, Download,
  ChevronLeft, ChevronRight
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from "recharts";
import AdminHeader from "../../Headers/AdminHeader";
import { getAdminPerformanceMetrics } from "../../redux/Actions/AdminProfessionalScoringActions";
import { RootState, AppDispatch } from "../../redux/store";

const PerformanceMetrics = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    performanceMetrics, 
    performanceMetricsLoading, 
    performanceMetricsError 
  } = useSelector((state: RootState) => state.adminProfessionalScoring);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showProfessionalDetails, setShowProfessionalDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [trendMonths, setTrendMonths] = useState(6); // 6 or 12 months
  
  // Debounce timer ref
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Debounced search effect
  useEffect(() => {
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for 500ms debounce
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    // Cleanup function
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery]);

  // Fetch data when page, debounced search, or trend months changes
  useEffect(() => {
    dispatch(getAdminPerformanceMetrics({
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchQuery || undefined,
      months: trendMonths,
    }));
  }, [dispatch, currentPage, itemsPerPage, debouncedSearchQuery, trendMonths]);

  // Use real data from Redux store
  const trendData = performanceMetrics?.trendData || [];
  const professionals = performanceMetrics?.professionals || [];
  const totalCount = performanceMetrics?.totalCount || 0;
  const totalPages = performanceMetrics?.totalPages || 1;
  const hasNextPage = performanceMetrics?.hasNextPage || false;

  // Calculate pagination display
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalCount);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // Loading state
  if (performanceMetricsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading performance metrics...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (performanceMetricsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{performanceMetricsError}</p>
          <button 
            onClick={() => dispatch(getAdminPerformanceMetrics({
              page: currentPage,
              limit: itemsPerPage,
              search: debouncedSearchQuery || undefined,
              months: trendMonths,
            }))} 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const ProfessionalCard = ({ professional }) => (
    <Card className="p-6 hover:shadow-md transition-shadow"   style={{ textAlign: "left" }}>
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg">{professional.name}</h3>
            <Badge variant={undefined} className={
              professional.status === "excellent" ? "bg-green-100 text-green-700" :
              professional.status === "good" ? "bg-blue-100 text-blue-700" :
              "bg-yellow-100 text-yellow-700"
            }>
              {professional.status.charAt(0).toUpperCase() + professional.status.slice(1)}
            </Badge>
          </div>
          <p className="text-gray-600">{professional.profession}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowProfessionalDetails(professional)}
        >
          <Eye className="h-4 w-4" />
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Star className="h-4 w-4" />
              Rating
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-2xl font-semibold">
                {professional.metrics.rating}
              </span>
              <Badge variant={undefined} className="bg-green-100 text-green-700">
                {professional.trends.rating}
              </Badge>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              Response Time
            </div>
            <div className="mt-1">
              <span className="text-2xl font-semibold">
                {professional.metrics.responseTime}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <ThumbsUp className="h-4 w-4" />
              Satisfaction
            </div>
            <div className="mt-1">
              <span className="text-2xl font-semibold">
                {professional.metrics.satisfactionScore}%
              </span>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Shield className="h-4 w-4" />
              Completion Rate
            </div>
            <div className="mt-1">
              <span className="text-2xl font-semibold">
                {professional.metrics.completionRate}%
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <Award className="h-4 w-4" />
          Achievements
        </div>
        <div className="flex flex-wrap gap-2">
          {professional.badges.map((badge, index) => (
            <Badge key={index} variant="outline">
              {badge}
            </Badge>
          ))}
        </div>
      </div>

      <div className="mt-6 pt-6 border-t grid grid-cols-3 gap-4">
        <div>
          <div className="text-sm text-gray-500">Total Jobs</div>
          <div className="font-semibold mt-1">
            {professional.metrics.totalJobs}
            <span className="text-green-600 text-sm ml-2">
              {professional.trends.jobs}
            </span>
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Emergency Jobs</div>
          <div className="font-semibold mt-1">
            {professional.metrics.emergencyJobs}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Revenue</div>
          <div className="font-semibold mt-1">
            {professional.metrics.revenueGenerated}
            <span className="text-green-600 text-sm ml-2">
              {professional.trends.revenue}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col"   style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Performance Metrics</h1>
                <p className="text-gray-600 mt-1">Monitor and analyze professional performance</p>
              </div>
              <Button 
                variant="outline"
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export Report
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Avg Rating</p>
                    <p className="text-2xl font-semibold mt-1">
                      {performanceMetrics?.averageRating || 0}
                    </p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Star className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Response Rate</p>
                    <p className="text-2xl font-semibold mt-1">
                      {performanceMetrics?.responseRate || 0}%
                    </p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Zap className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Completion Rate</p>
                    <p className="text-2xl font-semibold mt-1">
                      {performanceMetrics?.completionRate || 0}%
                    </p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Shield className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Top Performers</p>
                    <p className="text-2xl font-semibold mt-1">
                      {performanceMetrics?.topPerformers || 0}
                    </p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Award className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Trend Chart */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold">Performance Trends</h2>
                <select 
                  value={trendMonths}
                  onChange={(e) => setTrendMonths(Number(e.target.value))}
                  className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={6}>Last 6 months</option>
                  <option value={12}>Last 12 months</option>
                </select>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="rating" 
                      stroke="#4f46e5" 
                      strokeWidth={2}
                      name="Rating"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="completion" 
                      stroke="#16a34a"
                      strokeWidth={2}
                      name="Completion Rate"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="response" 
                      stroke="#db2777"
                      strokeWidth={2}
                      name="Response Rate"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Search */}
            <Card className="p-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search professionals by name or profession..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={handleClearSearch}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </div>
                {debouncedSearchQuery && debouncedSearchQuery !== searchQuery && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>Searching...</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Professionals Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {professionals.length > 0 ? (
                professionals.map(professional => (
                  <ProfessionalCard key={professional.id} professional={professional} />
                ))
              ) : (
                <div className="col-span-2 text-center py-12">
                  <p className="text-gray-500">No professionals found matching your criteria</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {endIndex} of {totalCount} professionals
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        // Show first page, last page, current page, and pages around current page
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-1 rounded ${
                                currentPage === page
                                  ? "bg-blue-600 text-white"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return <span key={page} className="px-2">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-1"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PerformanceMetrics;